import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { performanceMonitor } from './performanceMonitor';

// Global cache for quotes data
const quotesCache = new Map();
const cacheExpiry = 5 * 60 * 1000; // 5 minutes

// Event emitter for cache updates
const cacheListeners = new Set();

export const subscribeToCacheUpdates = (callback) => {
    cacheListeners.add(callback);
    return () => cacheListeners.delete(callback);
};

const notifyCacheUpdate = (orderId, quotes) => {
    cacheListeners.forEach(callback => callback(orderId, quotes));
};

/**
 * Fetch quotes for a single order with caching
 */
export const fetchQuotesForOrder = async (orderId) => {
    const cacheKey = `quotes_${orderId}`;
    const cached = quotesCache.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp) < cacheExpiry) {
        return cached.data;
    }

    try {
        const quotesQuery = query(
            collection(db, `orders/${orderId}/quotes`),
            orderBy('createdAt', 'desc')
        );
        const quotesSnapshot = await getDocs(quotesQuery);
        const quotes = quotesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Cache the result
        quotesCache.set(cacheKey, {
            data: quotes,
            timestamp: Date.now()
        });
        
        notifyCacheUpdate(orderId, quotes);
        return quotes;
    } catch (error) {
        console.error(`Error fetching quotes for order ${orderId}:`, error);
        return [];
    }
};

/**
 * Fetch quotes for multiple orders with batching and progress tracking
 */
export const fetchQuotesForOrders = async (orders, onProgress) => {
    if (!orders || orders.length === 0) {
        return {};
    }

    // Load quotes for all orders regardless of status
    const ordersNeedingQuotes = orders;

    if (ordersNeedingQuotes.length === 0) {
        return {};
    }

    const startTime = performance.now();
    const quotesMap = {};
    const batchSize = 10; // Process in batches to avoid overwhelming Firestore
    let processedCount = 0;

    try {
        // Process orders in batches
        for (let i = 0; i < ordersNeedingQuotes.length; i += batchSize) {
            const batch = ordersNeedingQuotes.slice(i, i + batchSize);
            
            // Check cache first
            const uncachedOrders = batch.filter(order => {
                const cacheKey = `quotes_${order.id}`;
                const cached = quotesCache.get(cacheKey);
                if (cached && (Date.now() - cached.timestamp) < cacheExpiry) {
                    quotesMap[order.id] = cached.data;
                    processedCount++;
                    return false;
                }
                return true;
            });

            // Fetch quotes for uncached orders
            const promises = uncachedOrders.map(async (order, index) => {
                try {
                    const quotes = await fetchQuotesForOrder(order.id);
                    quotesMap[order.id] = quotes;
                    const currentCount = processedCount + index + 1;
                    
                    if (onProgress) {
                        onProgress(currentCount, ordersNeedingQuotes.length);
                    }
                    
                    return { orderId: order.id, quotes };
                } catch (error) {
                    console.error(`Error fetching quotes for order ${order.id}:`, error);
                    quotesMap[order.id] = [];
                    const currentCount = processedCount + index + 1;
                    
                    if (onProgress) {
                        onProgress(currentCount, ordersNeedingQuotes.length);
                    }
                    
                    return { orderId: order.id, quotes: [] };
                }
            });

            // Wait for batch to complete
            await Promise.all(promises);
            
            // Update processed count after batch completes
            processedCount += uncachedOrders.length;
            
            // Small delay to prevent overwhelming the UI
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        const totalTime = performance.now() - startTime;
        performanceMonitor.trackQuotesLoading(ordersNeedingQuotes.length, totalTime);
        
        return quotesMap;
    } catch (error) {
        console.error('Error fetching quotes:', error);
        return quotesMap;
    }
};

/**
 * Clear the cache for a specific order or all orders
 */
export const clearQuotesCache = (orderId = null) => {
    if (orderId) {
        quotesCache.delete(`quotes_${orderId}`);
    } else {
        quotesCache.clear();
    }
};

/**
 * Get cached quotes for an order without fetching
 */
export const getCachedQuotes = (orderId) => {
    const cacheKey = `quotes_${orderId}`;
    const cached = quotesCache.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp) < cacheExpiry) {
        return cached.data;
    }
    
    return null;
};

/**
 * Prefetch quotes for orders that are likely to be viewed
 */
export const prefetchQuotes = async (orders) => {
    const ordersToPrefetch = orders.filter(order => 
        (order.status === 'In Progress' || order.status === 'Completed') &&
        !getCachedQuotes(order.id)
    );

    if (ordersToPrefetch.length > 0) {
        await fetchQuotesForOrders(ordersToPrefetch);
    }
}; 